import { useFeatures } from "./MapFeatures";
import { useAppContext } from "src/components/Context";
import { useCallback } from "react";
import mapboxgl, { PointLike } from "mapbox-gl";
import { useMapFiles } from "./GeoJSONFiles";

export function useHandleOnClick() {
    const features = useFeatures();
    const {data: mapFiles, isLoading: isMapFilesLoading} = useMapFiles();
    const { mapRef } = useAppContext();
    return useCallback(
      (
        e: mapboxgl.MapLayerMouseEvent,
        setClickedFeatures: (features: any[]) => void
      ) => {
        if (!mapRef || !mapRef.current || isMapFilesLoading) return [];
        // First check for points (points have priority over polygons)
        const sw = mapRef.current.unproject([e.point.x - 12, e.point.y + 12]);
        const ne = mapRef.current.unproject([e.point.x + 12, e.point.y - 12]);
  
        const bbox = [
          [sw.lng, sw.lat],
          [ne.lng, ne.lat],
        ] as [PointLike, PointLike];
        const mapFileFeatures = mapFiles?.flatMap((mapFile) => (mapFile as any).data.features.filter((f: any) => f.geometry.type === "Point")) ?? [];
        const clickedMapPoints = mapFileFeatures.filter((f: any) => isInBoundingBox(bbox, f.geometry.coordinates));
        const clickedPoints = [...features?.points?.filter(
          (f) =>
            f.geometry.coordinates &&
            isInBoundingBox(bbox, f.geometry.coordinates)
        ),...clickedMapPoints].slice(0,10);
  
        if (clickedPoints && clickedPoints.length > 0) {
          setClickedFeatures(clickedPoints);
          return;
        }
  
        // Only check for polygons if no points were clicked
        // Only check visible polygons (they're already filtered in useFeatures)
        const clickedPolygons = features?.polygons?.filter((polygon) => {
          const clickPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          return isPointInPolygonGeometry(clickPoint, polygon.geometry);
        }).slice(0,10);
  
        if (clickedPolygons && clickedPolygons.length > 0) {
          setClickedFeatures(clickedPolygons);
          return;
        }
  
        // If nothing was clicked, clear the selection
        setClickedFeatures([]);
      },
      [features, mapRef, isMapFilesLoading]
    );
  }
  

  function isInBoundingBox(bbox: [PointLike, PointLike], point: PointLike) {
    return (
      point[0] >= bbox[0][0] &&
      point[0] <= bbox[1][0] &&
      point[1] >= bbox[0][1] &&
      point[1] <= bbox[1][1]
    );
  }
  

  function isPointInPolygonGeometry(
    point: [number, number],
    geometry: any
  ): boolean {
    if (!geometry || !geometry.coordinates) return false;
  
    if (geometry.type === "Polygon") {
      // For Polygon, coordinates is an array of rings, use the first ring (exterior)
      return isPointInPolygon(point, geometry.coordinates[0] || []);
    } else if (geometry.type === "MultiPolygon") {
      // For MultiPolygon, check if point is in any of the polygons
      return geometry.coordinates.some((polygon: number[][][]) =>
        isPointInPolygon(point, polygon[0] || [])
      );
    }
  
    return false;
  }

  // Point-in-polygon algorithm using ray casting
function isPointInPolygon(
    point: [number, number],
    polygon: number[][]
  ): boolean {
    const [x, y] = point;
    let inside = false;
  
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
  
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
  
    return inside;
  }